import {
  cloneDeep,
  each,
  set,
  get,
  unset,
  values,
} from 'lodash';
import { IQueueElement } from '@/store/types';
import { ChangeStatusResult } from '@/service/types';
import order from '@/service/order';
import store from '@/store';

const DEV_MODE = process.env.NODE_ENV !== 'production';

class QueueProcess {
  store: any;
  queue: { [index: number]: IQueueElement } = {};
  running: { [index: number]: boolean } = {};
  possible: boolean = false;
  delay: any = 0;

  constructor(store: any) {
    this.store = store;
  }

  setPossible(possible: boolean) {
    this.possible = possible;
  }

  run() {
    this.getQueue();
    this.process();
  }

  private getQueue() {
    let arr = cloneDeep(this.store.getters['order/getQueue']);
    this.queue = {};
    each(arr, (item) => {
      this.queue[item.idOrder] = item;
    });
  }
  
  private async process() {
    each(this.queue, async (queueElement) => {
      if (this.isRunningSingle(queueElement.idOrder)) {
        if (DEV_MODE) console.log('tick: already enqueued ', queueElement.idOrder);
        return true;
      }
      this.setRunningSingle(queueElement.idOrder);
      if (DEV_MODE) console.log('tick: enqueueing ', queueElement.idOrder, this.isRunningSingle(queueElement.idOrder), this.queue);
      if (queueElement.actionType === 'confirm') {
        const status = await order.confirm(queueElement.idOrder);
        this.requestCallback(status, queueElement.idOrder);
      } else {
        const status = await order.reject(queueElement.idOrder);
        this.requestCallback(status, queueElement.idOrder);
      }
      if (DEV_MODE) console.log('tick: dequeued ', queueElement.idOrder, this.isRunningSingle(queueElement.idOrder), this.queue);
    });
  }

  private requestCallback(status: ChangeStatusResult, idOrder: number) {
    if (
      status === ChangeStatusResult.Ok
      || status === ChangeStatusResult.NotExists
    ) {
      this.cleanUpOrder(idOrder);
    }
    this.stopRunningSingle(idOrder);
    if (this.canRunAgain()) {
      if (this.delay) {
        clearTimeout(this.delay);
      }
      this.delay = setTimeout(() => {
        this.delay = 0;
        this.run();
      }, 5000);
    }
  }

  private cleanUpOrder(idOrder: number) {
    this.store.commit('order/SUBSTRACT_FROM_COLLECTION', idOrder);
    this.store.commit('order/FULFILL_ENQUEUED_REQUESTS', [ idOrder ]);
    this.stopRunningSingle(idOrder);
  }

  private setRunningSingle(idOrder: number) {
    set(this.running, idOrder, true);
  }

  private stopRunningSingle(idOrder: number) {
    unset(this.running, idOrder);
  }

  private isRunningSingle(idOrder: number) {
    return get(this.running, idOrder);
  }

  private canRunAgain() {
    let localQueue = values(this.getQueue());
    let stateQueue = values(this.store.getters['order/getQueue']);
    return !(localQueue.length) && stateQueue.length && this.possible;
  }
}

class QueueManager
{
  store: any;
  process: QueueProcess;
  active: boolean = false;
  watchdog: any;

  constructor(store: any) {
    this.store = store;
    this.process = new QueueProcess(store);
    this.store.subscribe((mutation: any, state: any) => {
      this.tick();
    });
    this.watchdog = setInterval(() => {
      this.tick();
    }, 15000);
  }

  private tick() {
    if (DEV_MODE) console.log('tick: called');
    if (this.isPossible() && this.active) {
      if (DEV_MODE) console.log('tick: possible to run');
      this.process.setPossible(true);
      this.process.run();
    } else {
      if (DEV_MODE) console.log('tick: imporssible to run');
      this.process.setPossible(false);
    }
  }

  private isPossible() {
    return this.store.getters['isOnline'] && this.store.getters['security/isLogged'];
  }

  start() {
    this.active = true;
    this.tick();
  }

  stop() {
    this.active = false;
  }
}


const guard: QueueManager = new QueueManager(store);

export default guard;